import { Box } from "@mui/material";
import { useState } from "react";
import { SwitchgearLabel } from "../hvSubstations/SwitchgearLabel";
import { ManagedSurveyAssetType } from "../shared/constants/managedSurveyAssetTypes";
import { RAGStatus } from "../shared/enums/Result";
import { getOverallRAGStatus } from "../shared/helpers/SubLocationHelpers";
import { IdName } from "../shared/interfaces/Asset";
import { Measurement, MeasurementDetail, Result, ResultSurveyableAsset } from "../shared/interfaces/Result";
import { TabView } from "../shared/TabView";
import { PanelMeasurementsOverview } from "./PanelMeasurementsOverview";
import { PartialResultReducer } from "./PartialResultReducer";

interface Props {
  result: Result;
  onIconClick: (measurement: MeasurementDetail<Measurement>, position: number | undefined, newTab: boolean) => void;
  onMediaClick: (selectedAsset?: IdName) => void;
  isPartialResult?: boolean;
  partialResultReducer?: PartialResultReducer;
  onImageUploadSuccess?: () => void;
  onImageUploadError?: (response: Response) => Promise<void>;
  assetType?: ManagedSurveyAssetType;
  canAddResult?: boolean;
  onAssetChange: (asset: ResultSurveyableAsset) => void;
}

function ragStatusCalculator(asset: ResultSurveyableAsset) {
  let assetRagStatus: RAGStatus = RAGStatus.None;
  asset.PanelMeasurements.forEach((panelMeasurement) => {
    const panelMeasurementRagStatus: RAGStatus = getOverallRAGStatus(panelMeasurement);
    if (panelMeasurementRagStatus === RAGStatus.Red) {
      assetRagStatus = RAGStatus.Red;
    } else if (assetRagStatus !== RAGStatus.Red && panelMeasurementRagStatus === RAGStatus.Amber) {
      assetRagStatus = RAGStatus.Amber;
    } else if (
      assetRagStatus !== RAGStatus.Red &&
      assetRagStatus !== RAGStatus.Amber &&
      panelMeasurementRagStatus === RAGStatus.Green
    ) {
      assetRagStatus = RAGStatus.Green;
    }
  });
  return assetRagStatus;
}

export function SurveyableAssetsOverview(props: Props) {
  const surveyableAssetsWithResults = props.result.dataValue.Assets.filter(
    (asset) => asset.PanelMeasurements.length > 0,
  );
  const [tab, setTab] = useState(0);
  const handleTabChange = (selectedTabIndex: number) => {
    setTab(selectedTabIndex);
    props.onAssetChange(surveyableAssetsWithResults[selectedTabIndex]);
  };

  if (props.result) {
    return (
      <TabView
        activeTabIndex={tab}
        onTabChange={handleTabChange}
        disableScrollButtons={surveyableAssetsWithResults.length === 1} // In partial results if there is only one asset, sometimes the scroll buttons appear but are not needed
        tabLabels={surveyableAssetsWithResults.map((asset: ResultSurveyableAsset, index) => (
          <SwitchgearLabel
            switchgear={asset}
            editing={false}
            isSelected={tab === index}
            status={ragStatusCalculator(asset)}
            partialResultSelectedAssetName={props.partialResultReducer?.state.selectedSwitchgears?.[index]?.name}
          />
        ))}
      >
        {surveyableAssetsWithResults.map((asset: ResultSurveyableAsset, index: number) => {
          return (
            <PanelMeasurementsOverview
              key={index}
              result={props.result}
              onIconClick={props.onIconClick}
              onMediaClick={props.onMediaClick}
              isPartialResult={props.isPartialResult}
              partialResultReducer={props.partialResultReducer}
              onImageUploadSuccess={props.onImageUploadSuccess}
              onImageUploadError={props.onImageUploadError}
              panelMeasurements={asset.PanelMeasurements}
              assetType={asset.AssetType ?? "Switchgear"}
              parentAssetId={
                asset.AssetId !== 0
                  ? asset.AssetId
                  : props.partialResultReducer?.state.selectedSwitchgears?.[index]?.assetId
              }
              canAddResult={props.canAddResult}
            />
          );
        })}
      </TabView>
    );
  } else {
    return <Box sx={{ height: "100vh" }}></Box>;
  }
}
